/**
 * TRACI-849: run `fn` every `ms` only while the tab is visible. A hidden tab makes no calls; when
 * it becomes visible again `fn` runs once at once and the cadence restarts from there. Callers
 * keep their own first call on mount. Without a DOM it is a plain setInterval.
 *
 * Returns a cleanup that stops the timer and removes the listener.
 */
export function startVisibleInterval(fn: () => void, ms: number): () => void {
  if (typeof document === 'undefined') {
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
  }
  let id: ReturnType<typeof setInterval> | null = null;
  const start = () => {
    if (id === null) id = setInterval(fn, ms);
  };
  const stop = () => {
    if (id !== null) {
      clearInterval(id);
      id = null;
    }
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      stop();
    } else if (id === null) {
      fn();
      start();
    }
  };
  if (document.visibilityState !== 'hidden') start();
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    stop();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
